import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CalendarRange,
  FileText,
  Loader2,
  PhoneIncoming,
  Plug,
  SlidersHorizontal,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusPill } from "@/components/settings/StatusPill";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AdsMccCheckCard } from "@/components/clients/AdsMccCheckCard";
import { ChannelSplitCard, ConnectionsCard, ManualSpendCard } from "@/components/clients/ContractOpsCards";
import { cn } from "@/lib/utils";
import { useLedger } from "@/hooks/usePlanner";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import {
  clientsService,
  type ClientDetail,
  type ClientIntegrationsResponse,
  type ClientLeadsResponse,
  type ClientWorkspacesResponse,
  type TenantIntegrations,
  type TenantWorkspace,
} from "@/services/clientsService";

/**
 * C3 of the clients consolidation: one client, whole. Overview = the points
 * ledger (the old Client Summary, now per-client instead of
 * workspace-switcher-scoped) + quick facts; Contract = the client_contracts
 * record, read-only during the dual-run — at Planner-webapp cutover this tab
 * becomes the editor for the Core-owned contract.
 */

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-[130px]">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border py-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value || "—"}</span>
    </div>
  );
}

function IntegrationRow({
  name,
  state,
  verified,
  note,
}: {
  name: string;
  state: "connected" | "action_needed" | "not_set_up";
  verified?: boolean;
  note?: string | null;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border py-2 text-sm">
      <span className="font-medium">{name}</span>
      <span className="flex items-center gap-2 text-right">
        {note ? <span className="max-w-[280px] truncate text-xs text-muted-foreground">{note}</span> : null}
        <StatusPill state={state} verified={verified} />
      </span>
    </div>
  );
}

/**
 * C4: the tenant_settings/tenant_integrations slice, one card per tenant
 * domain (a contract can cover several — never show one domain's status as
 * the client's). Read-only during the dual-run per D3: the Settings page
 * stays the editor, reached via the per-tenant "Open settings" door.
 */
function TenantIntegrationsCard({ t }: { t: TenantIntegrations }) {
  const navigate = useNavigate();
  const { workspaces, setCurrentWorkspace, selectTenantByWebsite } = useWorkspaceStore();

  const openSettings = () => {
    const ws = workspaces.find((w) => w.website === t.tenant_id);
    if (ws) setCurrentWorkspace(ws);
    else selectTenantByWebsite(t.tenant_id);
    navigate("/settings/integrations");
  };

  const ga = t.google_ads;
  const gaLinked = ga.status === "connected";
  const gaState = !ga.level
    ? ("not_set_up" as const)
    : gaLinked && ga.customer_id
      ? ("connected" as const)
      : ("action_needed" as const);
  const gaNote = !ga.level
    ? null
    : gaLinked && ga.customer_id
      ? `CID ${ga.customer_id} · ${ga.level} link`
      : gaLinked
        ? `${ga.level} link OK · no customer ID mapped`
        : (ga.last_error ?? `link ${ga.status ?? "missing"}`);

  const wp = t.wordpress;
  const wpState = wp.connected
    ? ("connected" as const)
    : wp.partial_creds
      ? ("action_needed" as const)
      : ("not_set_up" as const);

  const gd = t.google_docs;
  const gdState =
    gd.enabled && gd.folder_set
      ? ("connected" as const)
      : gd.enabled || gd.folder_set
        ? ("action_needed" as const)
        : ("not_set_up" as const);

  return (
    <Card className="border-0 p-4 shadow-sm">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold">{t.tenant_id}</h2>
        <Button variant="ghost" size="sm" className="h-8" onClick={openSettings}>
          <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" /> Open settings
        </Button>
      </div>
      <IntegrationRow name="Google Ads" state={gaState} verified={gaLinked} note={gaNote} />
      <IntegrationRow
        name="WordPress"
        state={wpState}
        verified={false}
        note={wp.connected ? "Key saved" : wp.partial_creds ? "Key entered, never verified" : null}
      />
      <IntegrationRow
        name="Google Docs"
        state={gdState}
        verified={false}
        note={gdState === "action_needed" ? (gd.enabled ? "Enabled, no folder" : "Folder set, upload off") : null}
      />
      <IntegrationRow name="Bing indexing" state={t.bing.key_set ? "connected" : "not_set_up"} verified={false} />
      {t.other_connections.map((c) => (
        <IntegrationRow
          key={c.provider}
          name={c.provider.toUpperCase()}
          state={c.status === "connected" ? "connected" : "action_needed"}
          verified={c.status === "connected"}
          note={c.external_account_id}
        />
      ))}
    </Card>
  );
}

/**
 * C5 — Workspace card: staff-visible summary of who has access to a tenant's
 * workspace and at what role. Editing memberships stays in the workspace
 * settings UI; this card is a rollup, same posture as TenantIntegrationsCard.
 */
function TenantWorkspaceCard({ t }: { t: TenantWorkspace }) {
  const navigate = useNavigate();
  const { workspaces, setCurrentWorkspace, selectTenantByWebsite } = useWorkspaceStore();

  const openSettings = () => {
    const ws = workspaces.find((w) => w.website === t.tenant_id);
    if (ws) setCurrentWorkspace(ws);
    else selectTenantByWebsite(t.tenant_id);
    navigate("/settings/team");
  };

  if (!t.workspace) {
    return (
      <Card className="border-0 p-4 shadow-sm">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h2 className="text-sm font-bold">{t.tenant_id}</h2>
          <StatusPill state="action_needed" verified={false} />
        </div>
        <p className="text-xs text-muted-foreground">
          No workspace linked to this tenant. Create one from Settings to grant access.
        </p>
      </Card>
    );
  }

  const ws = t.workspace;
  const members = ws.members;
  const owners = members.filter((m) => m.role === "owner").length;

  return (
    <Card className="border-0 p-4 shadow-sm">
      <div className="mb-1 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold">{ws.name || ws.slug}</h2>
          <p className="text-xs text-muted-foreground">
            {t.tenant_id} · {ws.is_active ? "active" : "inactive"} · {members.length} member{members.length === 1 ? "" : "s"}
          </p>
        </div>
        <Button variant="ghost" size="sm" className="h-8" onClick={openSettings}>
          <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" /> Open settings
        </Button>
      </div>
      {members.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          No members yet — nobody outside the platform admins can reach this workspace.
        </p>
      ) : (
        <div className="mt-2 divide-y divide-border">
          {members.map((m) => (
            <div key={m.email} className="flex items-center justify-between gap-3 py-1.5 text-xs">
              <div className="min-w-0">
                <div className="truncate font-medium">{m.display_name || m.full_name || m.email}</div>
                {(m.display_name || m.full_name) && (
                  <div className="truncate text-muted-foreground">{m.email}</div>
                )}
              </div>
              <Badge variant="outline" className="font-normal capitalize">{m.role}</Badge>
            </div>
          ))}
        </div>
      )}
      {owners === 0 && members.length > 0 && (
        <p className="mt-2 text-xs text-destructive">
          No owner — role changes and invites will fall to a platform admin.
        </p>
      )}
    </Card>
  );
}

function monthLabel(period: string): string {
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return period;
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export default function ClientDetailPage() {
  const { contractId = "" } = useParams();
  const [client, setClient] = useState<ClientDetail | null>(null);
  // 'notfound' ONLY on a real 404 — a transient 5xx/timeout must render as a
  // retryable error, never "Client not found" (bit Jacob live during a
  // restart: the page called a healthy client nonexistent).
  const [loadState, setLoadState] = useState<"loading" | "ready" | "notfound" | "error">("loading");
  const [tab, setTab] = useState<
    "overview" | "contract" | "integrations" | "workspaces" | "leads"
  >("overview");
  const [integrations, setIntegrations] = useState<ClientIntegrationsResponse | null>(null);
  const [integrationsError, setIntegrationsError] = useState(false);
  const [leadsData, setLeadsData] = useState<ClientLeadsResponse | null>(null);
  const [leadsError, setLeadsError] = useState(false);
  const [workspaces, setWorkspaces] = useState<ClientWorkspacesResponse | null>(null);
  const [workspacesError, setWorkspacesError] = useState(false);

  const loadClient = () => {
    setLoadState("loading");
    clientsService
      .detail(contractId)
      .then((d) => {
        setClient(d);
        setLoadState("ready");
      })
      .catch((err) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        setLoadState(status === 404 ? "notfound" : "error");
      });
  };

  // Quiet refetch after a write (no skeleton flash): the header's "Ad budget"
  // and the Billing card read the same detail row the split just moved.
  const refreshClient = () => {
    clientsService.detail(contractId).then(setClient).catch(() => { /* keep what we have */ });
  };

  useEffect(() => {
    loadClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  useEffect(() => {
    if (tab !== "integrations" || integrations) return;
    clientsService
      .integrations(contractId)
      .then(setIntegrations)
      .catch(() => setIntegrationsError(true));
  }, [tab, integrations, contractId]);

  useEffect(() => {
    if (tab !== "leads" || leadsData) return;
    clientsService
      .leads(contractId)
      .then(setLeadsData)
      .catch(() => setLeadsError(true));
  }, [tab, leadsData, contractId]);

  useEffect(() => {
    if (tab !== "workspaces" || workspaces) return;
    clientsService
      .workspaces(contractId)
      .then(setWorkspaces)
      .catch(() => setWorkspacesError(true));
  }, [tab, workspaces, contractId]);

  const tenant = client?.tenants[0]?.tenant_id ?? null;
  const { data: ledger = [], isLoading } = useLedger(tenant);

  if (loadState === "notfound") {
    return <div className="p-6 text-sm text-muted-foreground">Client not found.</div>;
  }
  if (loadState === "error") {
    return (
      <div className="p-6 text-sm">
        <span className="text-destructive">Couldn't load this client — the server didn't answer.</span>
        <button onClick={loadClient} className="ml-3 rounded border border-border px-2 py-1 text-xs hover:bg-muted">
          Retry
        </button>
      </div>
    );
  }
  if (loadState === "loading" || !client) {
    return (
      <div className="space-y-4 p-6">
        <div className="h-8 w-64 animate-pulse rounded bg-muted" />
        <div className="h-24 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  const now = new Date();
  const nowPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/clients" className="text-muted-foreground hover:text-foreground" aria-label="Back to all clients">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <Building2 className="h-6 w-6 text-coral" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">
            {client.name}
            {client.is_paused ? <Badge variant="outline" className="ml-2 font-normal">paused</Badge> : null}
            {client.archived ? <Badge variant="outline" className="ml-2 font-normal">archived</Badge> : null}
          </h1>
          <p className="text-sm text-muted-foreground">
            {client.tenants.map((t) => t.tenant_id).join(" · ") || "no tenant mapped"}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-6">
          <Fact label="Points / mo" value={client.monthly_points} />
          <Fact
            label="Ad budget"
            value={client.monthly_budget_dollars != null ? `$${Math.round(client.monthly_budget_dollars).toLocaleString("en-US")}` : null}
          />
          <Fact label="Cycle day" value={client.billing_cycle_start_day} />
          <Fact label="Ads CID" value={client.ads_customer_id} />
        </div>
      </div>

      <div className="flex gap-1 border-b border-border">
        {(["overview", "contract", "integrations", "workspaces", "leads"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              "flex items-center gap-1.5 rounded-t-md px-4 py-2 text-sm font-semibold capitalize",
              tab === t ? "border border-b-0 border-border bg-card text-coral-dark" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t === "overview" ? <CalendarRange className="h-4 w-4" /> : t === "contract" ? <FileText className="h-4 w-4" /> : t === "integrations" ? <Plug className="h-4 w-4" /> : <PhoneIncoming className="h-4 w-4" />}
            {t}
          </button>
        ))}
      </div>

      {tab === "leads" ? (
        <div className="space-y-4">
          {leadsError ? (
            <div className="p-6 text-sm text-muted-foreground">Couldn't load leads.</div>
          ) : !leadsData ? (
            <div className="p-6 text-sm text-muted-foreground">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading leads…
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-6">
                <Fact label="Leads" value={leadsData.summary.total} />
                <Fact label="Quotable" value={leadsData.summary.quotable_yes} />
                <Fact label="Quoted $" value={`$${Math.round(leadsData.summary.quote_value).toLocaleString("en-US")}`} />
                <Fact label="Sales $" value={`$${Math.round(leadsData.summary.sales_value).toLocaleString("en-US")}`} />
              </div>
              {leadsData.leads.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground">
                  {leadsData.sync_enabled
                    ? "No leads in the window for this client."
                    : "Core's lead cache is not enabled on this environment yet (WC_LEADS_SYNC_ENABLED) — until the WhatConverts lane cutover, leads live in the Planner webapp."}
                </div>
              ) : (
                <Card className="border-0 shadow-sm">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/20">
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Quotable</TableHead>
                          <TableHead className="text-right">Quote</TableHead>
                          <TableHead className="min-w-[200px]">Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leadsData.leads.map((l) => (
                          <TableRow key={l.id}>
                            <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                              {l.date_created ? String(l.date_created).slice(0, 10) : "—"}
                            </TableCell>
                            <TableCell className="text-xs">{l.lead_type ?? "—"}</TableCell>
                            <TableCell>
                              {l.contact_name || l.contact_company || l.contact_email || l.contact_phone || "—"}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {[l.lead_source, l.lead_medium].filter(Boolean).join(" / ") || "—"}
                            </TableCell>
                            <TableCell>
                              <span
                                className={cn(
                                  "rounded-full px-2 py-0.5 text-xs font-medium",
                                  l.quotable === "yes" && "bg-teal-light text-teal-dark",
                                  l.quotable === "no" && "bg-destructive/10 text-destructive",
                                  l.quotable === "pending" && "bg-amber-light text-amber-dark",
                                  (!l.quotable || l.quotable === "not_set") && "bg-muted text-muted-foreground",
                                )}
                              >
                                {l.quotable ?? "not set"}
                              </span>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {l.quote_value != null ? `$${l.quote_value}` : "—"}
                            </TableCell>
                            <TableCell
                              className="max-w-[280px] truncate text-xs text-muted-foreground"
                              title={l.reason ? `${l.reason} (${l.reason_labeler === "jev" ? "Jev" : "AI shadow"} label)` : undefined}
                            >
                              {l.reason ?? "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      ) : tab === "integrations" ? (
        <div className="space-y-4">
          {/* Read-only, flag-gated (hides itself on a 503). Outside the
              tenants branch below so a brand-new client with no tenant yet
              still sees it. It never saves a customer id. */}
          <AdsMccCheckCard variant="panel" />
          {integrationsError ? (
            <div className="p-6 text-sm text-muted-foreground">Couldn't load integration status.</div>
          ) : !integrations ? (
            <div className="p-6 text-sm text-muted-foreground">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading integrations…
            </div>
          ) : integrations.tenants.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              No tenant mapped — integrations live on a tenant. Map one on the roster first.
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                {integrations.tenants.map((t) => (
                  <TenantIntegrationsCard key={t.tenant_id} t={t} />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Read-only status view — edits stay in each tenant's Settings page during the dual-run.
              </p>
            </>
          )}
        </div>
      ) : tab === "workspaces" ? (
        <div className="space-y-4">
          {workspacesError ? (
            <div className="p-6 text-sm text-muted-foreground">Couldn't load workspaces.</div>
          ) : !workspaces ? (
            <div className="p-6 text-sm text-muted-foreground">
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading workspaces…
            </div>
          ) : workspaces.tenants.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              No tenant mapped — workspaces attach to tenants. Map one on the roster first.
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                {workspaces.tenants.map((t) => (
                  <TenantWorkspaceCard key={t.tenant_id} t={t} />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Read-only view — invitations and role changes stay in each workspace's Settings page.
              </p>
            </>
          )}
        </div>
      ) : tab === "overview" ? (
        <Card className="border-0 shadow-sm">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/20">
                  <TableHead className="min-w-[180px]">Month</TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead className="text-right">Carryover</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!tenant ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                      No tenant mapped — the ledger lives on a tenant. Map one on the roster first.
                    </TableCell>
                  </TableRow>
                ) : isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                      <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading ledger…
                    </TableCell>
                  </TableRow>
                ) : ledger.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                      No plan history yet — set a budget or add tasks on the Agile Plan.
                    </TableCell>
                  </TableRow>
                ) : (
                  ledger.map((row) => {
                    const active = row.billing_period === nowPeriod;
                    return (
                      <TableRow key={row.billing_period} className={cn(active && "bg-coral-light/50 font-medium")}>
                        <TableCell>
                          {monthLabel(row.billing_period)}
                          {active ? (
                            <span className="ml-2 rounded-full bg-coral px-1.5 py-0.5 text-[10px] font-semibold text-white">Active</span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{row.budget}</TableCell>
                        <TableCell className="text-right tabular-nums">{row.used}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{row.carryover}</TableCell>
                        <TableCell className={cn("text-right font-semibold tabular-nums", row.available < 0 ? "text-destructive" : "text-teal-dark")}>
                          {row.available}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <Card className="border-0 p-4 shadow-sm">
            <h2 className="mb-1 text-sm font-bold">Billing & pacing</h2>
            <Field label="Monthly points" value={client.monthly_points} />
            <Field label="Monthly ad budget" value={client.monthly_budget_dollars != null ? `$${client.monthly_budget_dollars}` : null} />
            <Field label="Billing cycle day" value={client.billing_cycle_start_day} />
            <Field label="Start date" value={client.start_date} />
            <Field label="Actual start" value={client.actual_start_date} />
            <Field label="Pacing carryover anchor" value={client.pacing_carryover_start} />
            <Field label="Agile pays ad spend" value={client.agile_pays_ad_spend == null ? "—" : client.agile_pays_ad_spend ? "Yes" : "No"} />
          </Card>
          <Card className="border-0 p-4 shadow-sm">
            <h2 className="mb-1 text-sm font-bold">Contacts & links</h2>
            <Field label="Main POC" value={client.main_poc} />
            <Field label="Contact email" value={client.contact_email} />
            <Field label="Client email" value={client.client_email} />
            <Field label="Client phone" value={client.client_phone} />
            <Field label="Teamwork project" value={client.teamwork_project_name ?? client.teamwork_project_id} />
            <Field label="WhatConverts account" value={client.wc_account_name ?? client.wc_profile_id} />
            <Field label="LSA customer id" value={client.lsa_customer_id} />
            <p className="mt-3 text-xs text-muted-foreground">
              Read-only during the dual-run — the Planner webapp owns these fields
              (three pacing fields refresh hourly; the rest were imported once
              {client.source_synced_at ? `, last sync ${String(client.source_synced_at).slice(0, 10)}` : ""}).
              At cutover this tab becomes the editor.
            </p>
          </Card>
          {client.contract_id ? (
            <>
              <div className="md:col-span-2">
                <ChannelSplitCard contractId={client.contract_id as string} onApplied={refreshClient} />
              </div>
              <ConnectionsCard client={client} onSaved={() => { /* mirror heals */ }} />
              <ManualSpendCard client={client} onSaved={() => { /* mirror heals */ }} />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
